import React from "react";
import { PenLine } from "lucide-react";
import { supabase } from "../lib/supabase";
import { HaloDeMarca, paletaDeColores } from "./HaloDeMarca";
import { CompletitudDeMarca, type PasoDeMarca } from "./CompletitudDeMarca";
import { ConexionesDeMarca } from "./ResumenDeConexiones";

/** Lo que puede faltar en "Completá tu marca" y abre su "Agregar". */
export type PasoFaltante = "logo" | "colores" | "tipografia" | "memoria" | "producto" | "modelo";

type DatosDeMarca = {
  paleta: string[];
  tieneTipografia: boolean;
  tieneMemoria: boolean;
  productos: number;
  modelos: number;
};

/**
 * Lo que la cabecera muestra o cuenta de la marca activa y que `useBrandProfile` no
 * trae: la paleta (`brand_profiles.colors`) y lo necesario para "Completá tu marca"
 * (tipografía, memoria, cuántos productos y modelos propios hay) —
 * ADR-2026-10-02-mi-marca-completitud.
 *
 * Se relee con `atlas:brand-changed`, que `CompanyData` emite al guardar nombre, rubro,
 * logo, colores, tipografía o memoria, y cuando cambia `version` (el tab: al volver de
 * Productos o Modelos puede haber uno nuevo). Los conteos son `head` (sin filas).
 * Mientras no llegó la primera lectura devuelve `null`, para no pintar un porcentaje
 * equivocado.
 *
 * Tres resguardos — ADR-2026-10-05-mi-marca-datos-de-la-cabecera-atados-a-la-marca:
 *  • Los datos se guardan con la marca de la que vinieron y sólo se devuelven para
 *    esa: al cambiar de marca no se ve la paleta ni el progreso de la anterior.
 *  • Gana la última lectura: dos `atlas:brand-changed` seguidos disparan dos, y la
 *    vieja puede volver después.
 *  • Si alguna consulta falla, se quedan los últimos valores conocidos: un conteo
 *    fallido no es "0 productos" ni una lectura fallida es "sin colores", y ofrecer
 *    "Agregar" lo que ya está cargado es peor que no actualizar.
 */
const useDatosDeMarca = (
  companyId: string | null,
  brandId: string | null,
  version: string,
): DatosDeMarca | null => {
  const clave = `${companyId}:${brandId}`;
  const [leidos, setLeidos] = React.useState<{ clave: string; datos: DatosDeMarca } | null>(
    null,
  );

  React.useEffect(() => {
    let cancelado = false;
    let ultima = 0;
    const leer = async () => {
      if (!companyId || !brandId) return;
      const esta = ++ultima;
      const [marca, config, productos, modelos] = await Promise.all([
        supabase
          .from("brand_profiles")
          .select("colors, fonts, context")
          .eq("id", brandId)
          .maybeSingle(),
        // La memoria vieja de la marca por defecto vive acá; `CompanyData` la muestra
        // si la marca no tiene `context` propio, así que cuenta igual.
        supabase
          .from("config")
          .select("media_agent_analysis")
          .eq("id_company", companyId)
          .maybeSingle(),
        // Mismo criterio que la grilla (`fetchUserProducts`): los de esta marca y los
        // que no tienen marca.
        supabase
          .from("variantes")
          .select("id, productos!inner(brand_id)", { count: "exact", head: true })
          .eq("activo", true)
          .eq("id_company", companyId)
          .or(`brand_id.eq.${brandId},brand_id.is.null`, { referencedTable: "productos" }),
        // Sólo los propios: los de Atlas (`id_company` nulo) los tiene todo el mundo.
        supabase
          .from("modelos")
          .select("id", { count: "exact", head: true })
          .eq("id_company", companyId),
      ]);
      if (cancelado || esta !== ultima) return;
      if (marca.error || config.error || productos.error || modelos.error) return;
      const fuentes = (marca.data?.fonts as { suggested?: unknown } | null)?.suggested;
      setLeidos({
        clave: `${companyId}:${brandId}`,
        datos: {
        paleta: paletaDeColores(marca.data?.colors),
        tieneTipografia: Array.isArray(fuentes) && fuentes.length > 0,
        tieneMemoria: !!(
          (marca.data?.context as string | null)?.trim() ||
          (config.data?.media_agent_analysis as string | null)?.trim()
        ),
        productos: productos.count ?? 0,
        modelos: modelos.count ?? 0,
        },
      });
    };
    void leer();
    window.addEventListener("atlas:brand-changed", leer);
    return () => {
      cancelado = true;
      window.removeEventListener("atlas:brand-changed", leer);
    };
  }, [companyId, brandId, version]);

  return leidos?.clave === clave ? leidos.datos : null;
};

/** Hasta dos iniciales del nombre, para cuando la marca no tiene logo. */
const iniciales = (nombre: string): string =>
  nombre
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join("") || "·";

/**
 * Cabecera de identidad de Mi marca —
 * ADR-2026-10-02-mi-marca-cabecera-de-identidad; en todos los anchos desde
 * ADR-2026-10-05-mi-marca-cabecera-en-mobile.
 *
 * Abajo de `lg` es la misma cabecera en chico: logo de 56px, el nombre (que abre el
 * cambio de marca: ahí no está la tarjeta de la sidebar), la pill de "Completá tu
 * marca" a todo el ancho y los chips debajo. Reemplaza a la tarjeta del selector de
 * marca y a las filas de integración que había en mobile.
 *
 * La pantalla abría con un "Marca" genérico y una columna de 440px con cuatro botones
 * apilados; la marca en sí —su logo, su nombre, sus colores— no aparecía en ningún
 * lado salvo adentro de "Información". Ahora es lo primero que se ve, y las
 * integraciones (`children`, los chips) van debajo: son conexiones DE esta marca.
 *
 * El nombre es el `<h1>` de la sección, así que hereda los 32px de `.shell-seccion h1`
 * y abre a la misma altura que el título de las demás secciones.
 *
 * ⚠️ El `<h1>` es hijo DIRECTO de la fila del logo, y la fila es una grilla (logo
 * ocupando dos renglones, nombre y datos en la segunda columna) en vez de un flex con
 * una columna de texto adentro. El shell le suma 40px de `padding-top` al padre directo
 * de cualquier `<h1>` (`.shell-seccion :has(> h1)`, el aire de los títulos de sección):
 * con la columna de texto ese padre era la columna sola, el texto bajaba 40px y el logo
 * quedaba arriba, descentrado. Con la grilla el aire es de la fila entera.
 */
export const CabeceraDeMarca = ({
  companyId,
  brandId,
  nombre,
  rubro,
  logoUrl,
  version,
  palabraModelo,
  onEditar,
  onAgregar,
  escritorio,
  onCambiarMarca,
  children,
}: {
  companyId: string | null;
  brandId: string | null;
  nombre: string;
  rubro: string | null;
  logoUrl: string | null;
  /** Cambia → se vuelven a contar productos y modelos (ver `useDatosDeMarca`). */
  version: string;
  /** "modelo" o "avatar", según el rubro (`palabraDeModelo`). */
  palabraModelo: string;
  onEditar: () => void;
  /**
   * El "Agregar" de cada paso de "Completá tu marca": abre directamente lo que falta
   * (ADR-2026-10-02-mi-marca-agregar-directo). Quién y cómo lo abre lo resuelve la
   * pantalla.
   */
  onAgregar: (que: PasoFaltante) => void;
  /**
   * ≥1024px. Decide la etiqueta del nombre: `<h1>` en el shell de escritorio, donde es
   * el título de la sección; abajo, el título es "Marca" (`MobilePageTitle`) y el
   * nombre va como `<h2>`.
   */
  escritorio: boolean;
  /** Abajo de `lg`, el nombre es el botón para cambiar de marca. */
  onCambiarMarca?: () => void;
  children?: React.ReactNode;
}) => {
  const datos = useDatosDeMarca(companyId, brandId, version);
  /**
   * La cabecera es su propio contexto de apilado (`isolate`, por el halo): el `z-30`
   * del menú de "Completá tu marca" sólo vale ADENTRO de ella, y lo posicionado que
   * viene después en la página —el select de tipografía de Información, por ejemplo—
   * se le pintaba encima. Mientras el menú está abierto, la cabecera entera sube de
   * capa; cerrado no compite con nada.
   */
  const [menuAbierto, setMenuAbierto] = React.useState(false);
  const paleta = datos?.paleta ?? [];
  const [logoRoto, setLogoRoto] = React.useState(false);
  React.useEffect(() => setLogoRoto(false), [logoUrl]);

  const tieneLogo = !!logoUrl && !logoRoto;
  const pasos: PasoDeMarca[] | null = datos && [
    { clave: "logo", etiqueta: "Logo", listo: tieneLogo, ir: () => onAgregar("logo") },
    { clave: "colores", etiqueta: "Paleta de colores", listo: datos.paleta.length > 0, ir: () => onAgregar("colores") },
    { clave: "tipografia", etiqueta: "Tipografía", listo: datos.tieneTipografia, ir: () => onAgregar("tipografia") },
    { clave: "memoria", etiqueta: "Memoria de la IA", listo: datos.tieneMemoria, ir: () => onAgregar("memoria") },
    { clave: "productos", etiqueta: "Tu primer producto", listo: datos.productos > 0, ir: () => onAgregar("producto") },
    { clave: "modelos", etiqueta: `Tu primer ${palabraModelo}`, listo: datos.modelos > 0, ir: () => onAgregar("modelo") },
  ];

  return (
    <div className={`relative isolate flex flex-col gap-3 lg:gap-5 ${menuAbierto ? "z-30" : ""}`}>
      {/* El halo de la marca (ADR-2026-10-02-mi-marca-halo-de-marca): compartido con Tienda online. */}
      <HaloDeMarca paleta={paleta} escritorio={escritorio} />
      {/* Columnas: logo · nombre y datos (tan anchos como el texto, pueden achicarse) ·
          resto. La pill de "Completá tu marca" va en la tercera, a la derecha del
          nombre y centrada con el bloque nombre + datos: es más alta que el título y en
          su misma línea lo empujaba hacia abajo. */}
      {/* Abajo de `lg`: logo · nombre y datos, y la pill en un renglón propio a todo
          el ancho (al lado del nombre no entra en un teléfono). */}
      <div className="grid min-w-0 grid-cols-[56px_minmax(0,1fr)] items-center gap-x-3.5 lg:grid-cols-[72px_minmax(0,max-content)_minmax(0,1fr)] lg:gap-x-5">
        <div className="relative col-start-1 row-span-2 row-start-1 h-14 w-14 overflow-hidden rounded-2xl bg-white shadow-[0_8px_24px_-10px_rgba(16,24,40,0.25),0_0_0_1px_rgba(16,24,40,0.06)] lg:h-[72px] lg:w-[72px] lg:rounded-[20px]">
          {logoUrl && !logoRoto ? (
            <img
              src={logoUrl}
              alt={`Logo de ${nombre}`}
              onError={() => setLogoRoto(true)}
              className="absolute inset-0 h-full w-full object-cover"
            />
          ) : (
            <div
              className="absolute inset-0 flex items-center justify-center text-[19px] lg:text-[24px] font-bold tracking-[-0.02em] text-white"
              style={{ background: paleta[0] ?? "#16181D" }}
            >
              {iniciales(nombre)}
            </div>
          )}
        </div>

        {escritorio ? (
          <h1 className="col-start-2 row-start-1 self-end truncate">{nombre}</h1>
        ) : onCambiarMarca ? (
          <h2 className="col-start-2 row-start-1 self-end min-w-0">
            <button
              type="button"
              onClick={onCambiarMarca}
              aria-haspopup="dialog"
              className="flex max-w-full items-center gap-1 text-left text-[21px] font-semibold leading-tight tracking-[-0.01em] text-brand-black transition-opacity active:opacity-70"
            >
              <span className="truncate">{nombre}</span>
              {/* El mismo glifo que el selector de marca y la tarjeta de la sidebar. */}
              <span className="material-symbols-rounded shrink-0 text-[20px] text-black/40">
                unfold_more
              </span>
            </button>
          </h2>
        ) : (
          <h2 className="col-start-2 row-start-1 self-end truncate text-[21px] font-semibold leading-tight tracking-[-0.01em] text-brand-black">
            {nombre}
          </h2>
        )}
        <div className="col-start-2 row-start-2 mt-1 lg:mt-1.5 self-start flex flex-wrap items-center gap-x-2.5 gap-y-1 lg:gap-x-3 lg:gap-y-1.5 text-[13px] lg:text-[13.5px] text-black/50">
          {rubro && <span>{rubro}</span>}
          {paleta.length > 0 && (
            <>
              {rubro && <span aria-hidden className="h-3.5 w-px bg-black/[0.12]" />}
              <span className="flex items-center" title="Paleta de la marca">
                {paleta.slice(0, 6).map((hex, i) => (
                  <span
                    key={`${hex}-${i}`}
                    // Borde interno además del aro: un color claro (blanco, crema)
                    // se perdía contra el fondo de la página.
                    className="-ml-1 h-4 w-4 lg:h-[18px] lg:w-[18px] rounded-full ring-2 ring-[#f9f9f9] first:ml-0"
                    style={{ background: hex, boxShadow: "inset 0 0 0 1px rgba(16,24,40,0.12)" }}
                  />
                ))}
              </span>
            </>
          )}
          <span aria-hidden className="h-3.5 w-px bg-black/[0.12]" />
          <button
            type="button"
            onClick={onEditar}
            className="inline-flex items-center gap-1.5 font-medium text-black/60 transition-colors hover:text-[#16181D]"
          >
            <PenLine className="h-3.5 w-3.5" />
            {/* "Información" sobra en un teléfono: el renglón es corto y el tab se
                llama así. */}
            Editar<span className="hidden lg:inline"> información</span>
          </button>
        </div>
        {/* Completa, en mobile no va: "Marca completa" no pide nada y ahí cada renglón
            cuenta — ADR-2026-10-05-mi-marca-completa-no-va-en-mobile. */}
        {pasos && (
          <div className={`${pasos.every((p) => p.listo) ? "hidden lg:block " : ""}col-span-2 row-start-3 mt-4 lg:col-span-1 lg:col-start-3 lg:row-span-2 lg:row-start-1 lg:mt-0 lg:mb-1 lg:ml-3 lg:self-end lg:justify-self-start`}>
            <CompletitudDeMarca pasos={pasos} onAbiertoChange={setMenuAbierto} />
          </div>
        )}
      </div>

      {/* Las integraciones en su propia fila, siempre: al lado del nombre caían
          abajo o arriba según el ancho, y la cabecera cambiaba de forma sola. */}
      {/* Abajo de `lg` las conexiones son un solo renglón que abre una hoja; en
          escritorio, los chips de siempre — ADR-2026-10-05-mi-marca-conexiones-en-un-renglon. */}
      {children && <ConexionesDeMarca>{children}</ConexionesDeMarca>}
    </div>
  );
};
